import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { LocalizationService } from '../../core/localization/localization.service';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesDocumentProfileFinanceComponent } from './sales-document-profile-finance.component';
import { SalesDocumentProfilePrintComponent } from './sales-document-profile-print.component';
import {
  CreateSalesDocumentProfilePayload,
  SalesApiService,
  SalesDocumentProfileFinanceConfig,
  UpdateSalesDocumentProfilePayload
} from './sales-api.service';

type EnumOption = { value: string; labelKey: string };

@Component({
  standalone: true,
  selector: 'ain-sales-document-profile-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CurrencySelectorComponent,
    SalesDocumentProfileFinanceComponent,
    SalesDocumentProfilePrintComponent
  ],
  template: `
    <ain-page [title]="isCreateMode() ? t('sales.documentProfiles.form.createTitle') : t('sales.documentProfiles.form.editTitle')" [description]="t('sales.documentProfiles.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/sales/document-profiles']">{{ t('catalog.action.backToList') }}</a>
          <button pButton type="button" [label]="t('common.save')" [disabled]="saving()" (click)="save()"></button>
        </div>

        <div class="tabs">
          <button type="button" [class.active]="activeTab() === 'general'" (click)="activeTab.set('general')">{{ t('sales.documentProfiles.tabs.general') }}</button>
          <button type="button" [class.active]="activeTab() === 'finance'" (click)="activeTab.set('finance')">{{ t('sales.documentProfiles.tabs.finance') }}</button>
          @if (!isCreateMode()) {
            <button type="button" [class.active]="activeTab() === 'print'" (click)="activeTab.set('print')">{{ t('sales.documentProfiles.tabs.print') }}</button>
          }
        </div>

        @if (activeTab() === 'general') {
          <section class="card">
            <div class="grid">
              <label>
                <span>{{ t('sales.documentProfiles.fields.code') }}</span>
                <input pInputText [(ngModel)]="form.code" [readonly]="!isCreateMode()" />
              </label>
              <label>
                <span>{{ t('sales.documentProfiles.fields.name') }}</span>
                <input pInputText [(ngModel)]="form.name" />
              </label>
              <label>
                <span>{{ t('sales.documentProfiles.fields.documentKind') }}</span>
                <p-select [options]="documentKinds" optionValue="value" [(ngModel)]="form.documentKind" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('sales.documentProfiles.fields.defaultCurrency') }}</span>
                <ain-currency-selector [(ngModel)]="form.defaultCurrencyCode" />
              </label>
              <label>
                <span>{{ t('sales.documentProfiles.fields.inventoryEffect') }}</span>
                <p-select [options]="inventoryEffects" optionValue="value" [(ngModel)]="form.inventoryEffect" appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '-' }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label class="check">
                <input type="checkbox" [(ngModel)]="form.issueFinancialDocument" />
                <span>{{ t('sales.documentProfiles.fields.issueFinancialDocument') }}</span>
              </label>
              @if (!isCreateMode()) {
                <label class="check">
                  <input type="checkbox" [(ngModel)]="form.isActive" />
                  <span>{{ t('sales.documentProfiles.active') }}</span>
                </label>
              }
            </div>
          </section>
        }

        @if (activeTab() === 'finance') {
          <ain-sales-document-profile-finance [(finance)]="financeConfig" [issueFinancialDocument]="form.issueFinancialDocument" />
        }

        @if (activeTab() === 'print' && !isCreateMode()) {
          <ain-sales-document-profile-print [profileId]="profileId()!" />
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; }
    .tabs { display: flex; gap: 8px; margin-bottom: 10px; flex-wrap: wrap; }
    .tabs button { border: 1px solid #d1d5db; background: #fff; border-radius: 8px; padding: 8px 12px; cursor: pointer; }
    .tabs button.active { background: #eff6ff; border-color: #93c5fd; color: #1d4ed8; font-weight: 600; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; background: #fff; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.check { grid-template-columns: auto 1fr; align-items: center; }
    .message.error { color: #b91c1c; }
    @media (max-width: 760px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class SalesDocumentProfileDetailComponent implements OnInit {
  private readonly api = inject(SalesApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly saving = signal(false);
  readonly isCreateMode = signal(false);
  readonly errorMessage = signal('');
  readonly activeTab = signal<'general' | 'finance' | 'print'>('general');
  readonly profileId = signal<string | null>(null);

  readonly documentKinds: EnumOption[] = [
    { value: 'SalesInvoice', labelKey: 'sales.documentProfiles.kinds.SalesInvoice' },
    { value: 'Proforma', labelKey: 'sales.documentProfiles.kinds.Proforma' },
    { value: 'CreditNote', labelKey: 'sales.documentProfiles.kinds.CreditNote' }
  ];

  readonly inventoryEffects: EnumOption[] = [
    { value: 'None', labelKey: 'sales.documentProfiles.inventory.None' },
    { value: 'Issue', labelKey: 'sales.documentProfiles.inventory.Issue' },
    { value: 'Receipt', labelKey: 'sales.documentProfiles.inventory.Receipt' }
  ];

  form = {
    code: '',
    name: '',
    documentKind: 'SalesInvoice',
    defaultCurrencyCode: 'IRR',
    inventoryEffect: 'Issue',
    issueFinancialDocument: true,
    isActive: true
  };

  financeConfig: SalesDocumentProfileFinanceConfig = this.emptyFinanceConfig();

  ngOnInit(): void {
    const profileId = this.route.snapshot.paramMap.get('profileId');
    this.isCreateMode.set(!profileId || profileId === 'new');
    if (this.isCreateMode()) {
      this.loading.set(false);
      return;
    }

    this.profileId.set(profileId);
    this.api.getDocumentProfile(profileId!).subscribe({
      next: profile => {
        this.form = {
          code: profile.code,
          name: profile.name,
          documentKind: profile.documentKind,
          defaultCurrencyCode: profile.defaultCurrencyCode ?? 'IRR',
          inventoryEffect: profile.inventoryEffect,
          issueFinancialDocument: profile.issueFinancialDocument,
          isActive: profile.isActive
        };
        this.financeConfig = this.mapFinanceConfig(profile.financeConfig);
        this.loading.set(false);
      },
      error: () => {
        this.errorMessage.set(this.t('sales.documentProfiles.errorLoad'));
        this.loading.set(false);
      }
    });
  }

  save(): void {
    this.saving.set(true);
    this.errorMessage.set('');
    const financeConfig = this.toPayloadFinanceConfig();

    if (this.isCreateMode()) {
      const payload: CreateSalesDocumentProfilePayload = {
        code: this.form.code,
        name: this.form.name,
        documentKind: this.form.documentKind,
        defaultCurrencyCode: this.form.defaultCurrencyCode || null,
        inventoryEffect: this.form.inventoryEffect,
        issueFinancialDocument: this.form.issueFinancialDocument,
        financeConfig
      };
      this.api.createDocumentProfile(payload).subscribe({
        next: profile => void this.router.navigate(['/sales/document-profiles', profile.id]),
        error: () => this.failSave()
      });
      return;
    }

    const profileId = this.route.snapshot.paramMap.get('profileId')!;
    const payload: UpdateSalesDocumentProfilePayload = {
      name: this.form.name,
      documentKind: this.form.documentKind,
      defaultCurrencyCode: this.form.defaultCurrencyCode || null,
      inventoryEffect: this.form.inventoryEffect,
      issueFinancialDocument: this.form.issueFinancialDocument,
      isActive: this.form.isActive,
      financeConfig
    };
    this.api.updateDocumentProfile(profileId, payload).subscribe({
      next: () => this.saving.set(false),
      error: () => this.failSave()
    });
  }

  private toPayloadFinanceConfig(): SalesDocumentProfileFinanceConfig | null {
    const factors = this.financeConfig.adjustmentFactors
      .filter(row => row.name.trim() && row.accountId)
      .map((row, index) => ({
        ...row,
        name: row.name.trim(),
        sortOrder: index + 1
      }));

    const hasBook = !!this.financeConfig.ledgerBookId;
    const hasPosting = !!(
      this.financeConfig.postingAccounts.receivableAccountId ||
      this.financeConfig.postingAccounts.revenueAccountId ||
      this.financeConfig.postingAccounts.inventoryAccountId ||
      this.financeConfig.postingAccounts.costOfSalesAccountId
    );

    if (!hasBook && !hasPosting && factors.length === 0) {
      return null;
    }

    return {
      ledgerBookId: this.financeConfig.ledgerBookId,
      postingAccounts: { ...this.financeConfig.postingAccounts },
      adjustmentFactors: factors
    };
  }

  private mapFinanceConfig(config?: SalesDocumentProfileFinanceConfig | null): SalesDocumentProfileFinanceConfig {
    if (!config) {
      return this.emptyFinanceConfig();
    }

    return {
      ledgerBookId: config.ledgerBookId,
      postingAccounts: {
        receivableAccountId: config.postingAccounts?.receivableAccountId,
        revenueAccountId: config.postingAccounts?.revenueAccountId,
        inventoryAccountId: config.postingAccounts?.inventoryAccountId,
        costOfSalesAccountId: config.postingAccounts?.costOfSalesAccountId
      },
      adjustmentFactors: (config.adjustmentFactors ?? []).map(factor => ({ ...factor }))
    };
  }

  private emptyFinanceConfig(): SalesDocumentProfileFinanceConfig {
    return {
      postingAccounts: {},
      adjustmentFactors: []
    };
  }

  private failSave(): void {
    this.errorMessage.set(this.t('sales.documentProfiles.errorSave'));
    this.saving.set(false);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
